import { Box, Center, Flex, Text } from "@chakra-ui/react";
import React, { useMemo } from "react";

import { LoadingIndicator } from "../../../../components";
import { formatDateRange, MS_PER_DAY } from "../../../../utils/datetime";
import {
  AiInterviewFeedbackRatingSummaryQuery,
  useAiInterviewFeedbackRatingSummaryQuery,
} from "../../../graphql";
import AnalyticsLineChart from "../../../pages/analytics/line-chart/AnalyticsLineChart";
import type { LineChartDataPoint } from "../../../pages/analytics/types";
import {
  computePreviousDateRange,
  getUnixFromISO,
} from "../../../pages/analytics/utils";

const THREE_WEEKS_MS = 21 * 24 * 60 * 60 * 1000;

const CHART_LINE_COLOR = "#6BA4D9";

export type RatingChartDatum = {
  avgRating: number;
  comparisonBucketStart?: string;
  reviewCount: number;
  xLabel: string;
};

export const getFeedbackRatingBucketInterval = (
  dateStart: Date,
  dateEnd: Date
): "DAY" | "WEEK" =>
  dateEnd.getTime() - dateStart.getTime() >= THREE_WEEKS_MS ? "WEEK" : "DAY";

const RatingYAxisTick: React.FC<any> = ({ x, y, payload }) => (
  <g transform={`translate(${x},${y})`}>
    <text x={0} y={0} dy={4} textAnchor="end" fontSize="0.75rem" fill="#67717E">
      {payload.value}
    </text>
    <text
      x={2}
      y={0}
      dy={3}
      textAnchor="start"
      fontSize="0.5rem"
      fill="#FFA300"
    >
      ★
    </text>
  </g>
);

export const formatReviewCount = (reviewCount: number): string =>
  `${reviewCount}`;

export const formatChangeLabel = (formattedLabel: string): string =>
  formattedLabel.startsWith("Week of")
    ? `week of${formattedLabel.slice("Week of".length)}`
    : formattedLabel;

export const formatPctChange = (
  currentValue: number,
  previousValue: number | undefined
): string => {
  if (previousValue == null || previousValue === 0) {
    return "--";
  }

  const pctChange = ((currentValue - previousValue) / previousValue) * 100;
  const roundedPctChange = Math.round(pctChange);
  const signPrefix = roundedPctChange > 0 ? "+" : "";

  return `${signPrefix}${roundedPctChange}%`;
};

const parseDateKey = (dateKey: string): Date => {
  const [year, month, day] = dateKey.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const formatDateKey = (date: Date): string =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

export const shiftDateKeyByDays = (dateKey: string, days: number): string => {
  const nextDate = parseDateKey(dateKey);
  nextDate.setDate(nextDate.getDate() + days);
  return formatDateKey(nextDate);
};

export const findComparisonPoint = <T extends { date: string }>({
  bucketInterval,
  currentPointDate,
  previousPoints,
  selectedRangeDays,
}: {
  bucketInterval: "DAY" | "WEEK";
  currentPointDate: string;
  previousPoints: T[];
  selectedRangeDays: number;
}): T | undefined => {
  const targetDate = shiftDateKeyByDays(currentPointDate, -selectedRangeDays);

  if (bucketInterval === "DAY") {
    return previousPoints.find((point) => point.date === targetDate);
  }

  return previousPoints.find((point) => {
    const bucketStart = point.date;
    const bucketEnd = shiftDateKeyByDays(bucketStart, 7);

    return bucketStart <= targetDate && targetDate < bucketEnd;
  });
};

export const buildTooltipDetailRows = ({
  datum,
  compareDatum,
  formatLabel,
}: {
  datum: RatingChartDatum;
  compareDatum?: RatingChartDatum;
  formatLabel: (iso: string) => string;
}): Array<{
  label: string;
  value: string;
}> => [
  {
    label: "Reviews",
    value: formatReviewCount(datum.reviewCount),
  },
  {
    label: compareDatum?.xLabel
      ? `Change vs ${formatChangeLabel(formatLabel(compareDatum.xLabel))}`
      : datum.comparisonBucketStart
      ? `Change vs ${formatChangeLabel(
          formatLabel(datum.comparisonBucketStart)
        )}`
      : "Change vs previous period",
    value: formatPctChange(datum.avgRating, compareDatum?.avgRating),
  },
];

type AiInterviewerFeedbackRatingCardProps = {
  aiInterviewerId: string;
  bucketInterval?: "DAY" | "WEEK";
  dateStart: Date;
  dateEnd: Date;
  loadingSummary?: boolean;
  summary?: NonNullable<
    AiInterviewFeedbackRatingSummaryQuery["aiInterviewFeedbackRatingSummary"]
  >;
};

export const AiInterviewerFeedbackRatingCard: React.FC<
  AiInterviewerFeedbackRatingCardProps
> = ({
  aiInterviewerId,
  bucketInterval: bucketIntervalProp,
  dateStart,
  dateEnd,
  loadingSummary,
  summary,
}) => {
  const bucketInterval =
    bucketIntervalProp ?? getFeedbackRatingBucketInterval(dateStart, dateEnd);
  const usesProvidedSummary =
    loadingSummary !== undefined || summary !== undefined;
  const selectedRangeDays = useMemo(
    () =>
      Math.floor((dateEnd.getTime() - dateStart.getTime()) / MS_PER_DAY) + 1,
    [dateEnd, dateStart]
  );
  const { previousDateRangeStart, previousDateRangeEnd } = useMemo(
    () => computePreviousDateRange(dateStart, dateEnd),
    [dateEnd, dateStart]
  );

  const { data, loading } = useAiInterviewFeedbackRatingSummaryQuery({
    variables: {
      aiInterviewerId,
      dateStart: dateStart.toISOString(),
      dateEnd: dateEnd.toISOString(),
      bucketInterval,
    },
    skip: usesProvidedSummary,
  });
  const { data: previousData } = useAiInterviewFeedbackRatingSummaryQuery({
    variables: {
      aiInterviewerId,
      dateStart: previousDateRangeStart.toISOString(),
      dateEnd: previousDateRangeEnd.toISOString(),
      bucketInterval,
    },
    skip:
      (usesProvidedSummary && (loadingSummary ?? false)) ||
      (usesProvidedSummary && (summary?.reviewCount ?? 0) === 0),
  });

  const currentSummary = usesProvidedSummary
    ? summary
    : data?.aiInterviewFeedbackRatingSummary;
  const currentSummaryLoading = usesProvidedSummary
    ? loadingSummary ?? false
    : loading;
  const previousSummary = previousData?.aiInterviewFeedbackRatingSummary;

  const chartData = useMemo(() => {
    if (!currentSummary?.timeSeries) return [];

    const previousPoints = previousSummary?.timeSeries ?? [];

    return currentSummary.timeSeries
      .filter((point) => point.reviewCount > 0)
      .map((point) => {
        const comparisonPoint = findComparisonPoint({
          bucketInterval,
          currentPointDate: point.date,
          previousPoints,
          selectedRangeDays,
        });

        return {
          x: getUnixFromISO(point.date),
          datum: {
            avgRating: point.avgRating,
            comparisonBucketStart:
              comparisonPoint?.date ??
              shiftDateKeyByDays(point.date, -selectedRangeDays),
            reviewCount: point.reviewCount,
            xLabel: point.date,
          },
          compareDatum: comparisonPoint
            ? {
                avgRating: comparisonPoint.avgRating,
                reviewCount: comparisonPoint.reviewCount,
                xLabel: comparisonPoint.date,
              }
            : undefined,
        };
      });
  }, [
    bucketInterval,
    currentSummary?.timeSeries,
    previousSummary?.timeSeries,
    selectedRangeDays,
  ]);

  const dateRangeLabel = formatDateRange(dateStart, dateEnd);

  if (currentSummaryLoading && !currentSummary) {
    return (
      <Box
        border="1px solid"
        borderColor="gray.300"
        borderRadius="12px"
        p={6}
        bg="white"
      >
        <Box
          display="grid"
          gridTemplateColumns="260px 80px minmax(0, 1fr)"
          gridTemplateRows="auto auto"
          rowGap={4}
          alignItems="start"
        >
          <Box gridColumn="1" gridRow="1">
            <Text fontWeight="semibold" whiteSpace="nowrap">
              Feedback rating (avg)
            </Text>
          </Box>

          <Box gridColumn="3" gridRow="1">
            <Text fontSize="sm" color="gray.600" textAlign="right">
              {dateRangeLabel}
            </Text>
          </Box>

          <Center gridColumn="1 / 4" gridRow="2" py="80px">
            <LoadingIndicator delay={0} />
          </Center>
        </Box>
      </Box>
    );
  }

  if (!currentSummary) return null;

  const distMap = new Map(
    currentSummary.distribution.map((d) => [d.rating, d])
  );

  return (
    <Box
      border="1px solid"
      borderColor="gray.300"
      borderRadius="12px"
      p={6}
      bg="white"
    >
      <Box
        display="grid"
        gridTemplateColumns="260px 80px minmax(0, 1fr)"
        gridTemplateRows="auto auto auto"
        rowGap={4}
        alignItems="start"
      >
        <Box gridColumn="1" gridRow="1">
          <Text whiteSpace="nowrap">
            <Text as="span" fontWeight="semibold">
              Feedback rating (avg)
            </Text>{" "}
            <Text as="span" fontSize="12px" color="gray.500">
              (
              <Text as="span" fontStyle="italic">
                n
              </Text>
              ={currentSummary.reviewCount})
            </Text>
          </Text>
        </Box>

        <Box gridColumn="3" gridRow="1">
          <Text fontSize="sm" color="gray.600" textAlign="right">
            {dateRangeLabel}
          </Text>
        </Box>

        <Flex gridColumn="1" gridRow="2" alignItems="baseline" gap={1}>
          <Text
            fontSize="42px"
            fontWeight={500}
            color="#393F47"
            lineHeight="56px"
          >
            {currentSummary.avgRating != null
              ? currentSummary.avgRating.toFixed(1)
              : "--"}
          </Text>
          <Text fontSize="24px" color="#FFA300" lineHeight="24px">
            ★
          </Text>
          <Text fontSize="12px" color="gray.500" ml={1}>
            ({currentSummary.reviewCount} review
            {currentSummary.reviewCount !== 1 ? "s" : ""})
          </Text>
        </Flex>

        <Flex
          gridColumn="1"
          gridRow="3"
          direction="column"
          gap="2px"
          mt="-10px"
          width="260px"
          pr="44px"
          boxSizing="border-box"
        >
          {[5, 4, 3, 2, 1].map((rating) => {
            const entry = distMap.get(rating);
            const pct = entry?.pct ?? 0;
            return (
              <Flex key={rating} gap="6px" alignItems="center" width="100%">
                <Text
                  fontSize="12px"
                  fontWeight={500}
                  color="#67717E"
                  width="8px"
                  textAlign="right"
                >
                  {rating}
                </Text>
                <Box
                  flex={1}
                  bg="#E5E7EA"
                  borderRadius="999px"
                  overflow="hidden"
                >
                  <Box
                    height="6px"
                    bg="#FFB533"
                    borderRadius="999px"
                    width={`${pct}%`}
                  />
                </Box>
                <Text
                  fontSize="12px"
                  fontWeight={500}
                  color="#67717E"
                  width="24px"
                  textAlign="right"
                  whiteSpace="nowrap"
                >
                  {pct}%
                </Text>
              </Flex>
            );
          })}
        </Flex>

        {chartData.length > 0 && (
          <Box
            gridColumn="3"
            gridRow="2 / 4"
            minW={0}
            h="100%"
            pt="24px"
            overflow="visible"
            sx={{
              "& .recharts-wrapper, & .recharts-surface": {
                overflow: "visible !important",
              },
              "& .recharts-dot": {
                fill: "white",
                stroke: CHART_LINE_COLOR,
                strokeWidth: 2,
              },
            }}
          >
            <AnalyticsLineChart
              data={chartData as unknown as LineChartDataPoint[]}
              dataKey="datum.avgRating"
              yAxis={{
                domain: [1, 5],
                ticks: [1, 2, 3, 4, 5],
              }}
              yAxisTick={<RatingYAxisTick />}
              drawDot
              bucketInterval={bucketInterval}
              bucketSize={1}
              tooltipVersion="myInsights"
              tooltipDetailsFormatter={({
                datum,
                compareDatum,
                formatLabel,
              }) => {
                return buildTooltipDetailRows({
                  datum: datum as RatingChartDatum,
                  compareDatum: compareDatum as RatingChartDatum | undefined,
                  formatLabel,
                });
              }}
              rangeDisplayValues={{
                current: dateRangeLabel,
                previous: "",
              }}
              hideLegend
              lineColor={CHART_LINE_COLOR}
              lineStrokeWidth={2}
              chartMargin={{ top: 0, right: 0, left: 0, bottom: 0 }}
              chartHeight="100%"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
